import { Box, TextField } from "@mui/material";
import dayjs from "dayjs";
import CustomDatePicker from "@/components/CustomDatePicker";
import Text from "@/components/Text";
import ChooseOneGroup from "@/pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/encounter/note-forms/readmission-f2f/components/ChooseOneGroup";
import MultiSelectGroup from "@/pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/encounter/note-forms/readmission-f2f/components/MultiSelectGroup";
import { FREE_TEXT_FORM } from "./assessment-constant";
import { FreeTextQuestionDef, QuestionValue } from "./assessment-types";
import { questionIssue } from "./free-text-screening";

export interface FreeTextQuestionCardProps {
  q: FreeTextQuestionDef;
  title: string;
  value: QuestionValue;
  disabled?: boolean;
  onChange: (value: QuestionValue) => void;
}

/** One screener question, drawn with the same option groups the visit notes use. */
const FreeTextQuestionCard = ({
  q,
  title,
  value,
  disabled,
  onChange,
}: FreeTextQuestionCardProps) => {
  const placeholder = q.placeholder ?? FREE_TEXT_FORM.notesPlaceholder;

  if (q.kind === "single" || q.kind === "yesNo") {
    return (
      <ChooseOneGroup
        title={title}
        options={q.options ?? []}
        disabled={disabled}
        freeTextPlaceholder={placeholder}
        isInline={q.kind === "yesNo"}
        withFreeText={!q.noteShownWhen || value.answers[0] === q.noteShownWhen}
        value={{ selected: value.answers[0] ?? "", freeText: value.notes }}
        onChange={(next) =>
          onChange({
            answers: next.selected ? [next.selected] : [],
            notes:
              q.noteShownWhen && next.selected !== q.noteShownWhen
                ? ""
                : next.freeText,
          })
        }
      />
    );
  }

  if (q.kind === "multi" || q.kind === "confirm") {
    const isConfirm = q.kind === "confirm";
    const options = isConfirm ? [q.statement ?? ""] : (q.options ?? []);
    const selected = isConfirm
      ? value.answers[0] === "Yes"
        ? options
        : []
      : value.answers;
    return (
      <MultiSelectGroup
        title={title}
        options={options}
        disabled={disabled}
        freeTextPlaceholder={placeholder}
        withDetails={false}
        exclusive={
          q.exclusiveOption
            ? {
                [q.exclusiveOption]: options.filter(
                  (it) => it !== q.exclusiveOption
                ),
              }
            : undefined
        }
        value={{ selected, details: {}, freeText: value.notes }}
        onChange={(next) =>
          onChange({
            answers: isConfirm
              ? next.selected.length
                ? ["Yes"]
                : []
              : next.selected,
            notes: next.freeText,
          })
        }
      />
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={1}
      p={1.5}
      sx={{
        border: `1px solid ${FREE_TEXT_FORM.borderColor}`,
        borderRadius: 1,
      }}
    >
      <Text fontWeight="bold">{title}</Text>
      {q.kind === "date" ? (
        <Box width={FREE_TEXT_FORM.inputWidth}>
          <CustomDatePicker
            value={value.answers[0] ?? null}
            disabled={disabled}
            minDate={
              q.dateRule === "notPast" ? dayjs().startOf("day") : undefined
            }
            maxDate={
              q.dateRule === "notFuture" ? dayjs().endOf("day") : undefined
            }
            // Out-of-range typed dates stay and show why, instead of silently becoming today.
            clampToRange={false}
            error={
              value.answers[0] ? questionIssue(q, title, value) : undefined
            }
            onChange={(date) =>
              onChange({
                ...value,
                answers: date ? [date.toISOString()] : [],
              })
            }
          />
        </Box>
      ) : (
        <TextField
          size="small"
          type={q.kind}
          fullWidth={q.kind === "text"}
          sx={
            q.kind === "text" ? undefined : { width: FREE_TEXT_FORM.inputWidth }
          }
          disabled={disabled}
          placeholder={q.kind === "text" ? q.placeholder : undefined}
          multiline={q.isMultiline}
          minRows={q.isMultiline ? FREE_TEXT_FORM.multilineRows : undefined}
          maxRows={q.isMultiline ? FREE_TEXT_FORM.maxRows : undefined}
          value={value.answers[0] ?? ""}
          onChange={(e) =>
            onChange({
              ...value,
              answers: e.target.value ? [e.target.value] : [],
            })
          }
          inputProps={
            q.kind === "number"
              ? { min: 0, max: FREE_TEXT_FORM.numberMax }
              : undefined
          }
        />
      )}
      {q.kind !== "text" && (
        <TextField
          size="small"
          fullWidth
          disabled={disabled}
          placeholder={placeholder}
          value={value.notes}
          onChange={(e) => onChange({ ...value, notes: e.target.value })}
        />
      )}
    </Box>
  );
};

export default FreeTextQuestionCard;
